import { useState } from 'react'

import { useToast } from '@/hooks'
import { captureException } from '@/services/monitoring/captureException'
import type { Report } from '../../../stores/types'
import { collectChartImages } from './chartImages'
import { applyChartViews, collectChartViews } from './chartViews'
import { buildReportDocument } from './reportBlocks'

export type ReportExportFormat = 'pdf' | 'docx'

// Orchestrates one report export: build the shared block model, rasterize
// the on-screen charts, then hand off to the format renderer (both heavy
// bundles are lazy-imported). Exposes which format is in flight so the
// download control can disable itself.
export function useReportExport(
  report: Report | null | undefined,
  baseFilename: string,
) {
  const [exporting, setExporting] = useState<ReportExportFormat | null>(null)
  const { errorToast } = useToast()

  async function run(format: ReportExportFormat): Promise<void> {
    if (exporting || !report) return
    setExporting(format)
    try {
      const chartIds = collectChartIds(report)
      const reportDocument = buildReportDocument(
        applyChartViews(report, collectChartViews(chartIds)),
      )
      const chartImages = await collectChartImages(chartIds)
      const filename = `${baseFilename}.${format}`
      if (format === 'pdf') {
        const { exportReportPdf } = await import('./exportReportPdf')
        await exportReportPdf(reportDocument, chartImages, filename)
      } else {
        const { exportReportDocx } = await import('./exportReportDocx')
        await exportReportDocx(reportDocument, chartImages, filename)
      }
    } catch (error) {
      captureException(
        error instanceof Error ? error : new Error(String(error)),
      )
      errorToast(
        'Export failed',
        'We could not build the report file. Please try again.',
      )
    } finally {
      setExporting(null)
    }
  }

  return {
    exporting,
    exportPdf: () => run('pdf'),
    exportDocx: () => run('docx'),
  }
}

function collectChartIds(report: Report): string[] {
  return report.sections.flatMap((section) =>
    (section.charts ?? [])
      .map((chart) => chart.id ?? '')
      .filter((id): id is string => Boolean(id)),
  )
}
